import { BillingInterval, PLAN_CONFIG, PlanKey } from '@/constants';

export type EducationDiscountStatus = 'not_edu' | 'unverified_edu' | 'verified_edu';

export interface EducationDiscountPreview {
    discountedTotal: number | null;
    hasDiscount: boolean;
    originalTotal: number | null;
}

const EDUCATION_PLAN_PRICING = {
    annual: 65 * 12,
    monthly: 75,
} as const satisfies Record<BillingInterval, number>;

export const getSeatPrice = (plan: PlanKey, interval: BillingInterval): number | null => {
    const config = PLAN_CONFIG[plan];

    if (!config.pricing) {
        return null;
    }

    return interval === 'monthly' ? config.pricing.monthly.unitAmount : config.pricing.annual.yearlyAmount;
};

export const calculateSeatTotal = (plan: PlanKey, interval: BillingInterval, seats: number): number | null => {
    const price = getSeatPrice(plan, interval);

    return price === null ? null : price * seats;
};

export const isEducationEligible = (emailAddress?: string): boolean =>
    /^[^@\s]+@[^@\s]+\.edu$/iu.test(emailAddress?.trim() ?? '');

export const getEducationDiscountStatus = (
    emailAddress: string | undefined,
    emailConfirmed: boolean | undefined,
): EducationDiscountStatus => {
    if (!isEducationEligible(emailAddress)) {
        return 'not_edu';
    }

    return emailConfirmed ? 'verified_edu' : 'unverified_edu';
};

export const calculateEducationDiscountPreview = (
    plan: PlanKey,
    interval: BillingInterval,
    seats: number,
    status: EducationDiscountStatus,
): EducationDiscountPreview => {
    const originalTotal = calculateSeatTotal(plan, interval, seats);

    if (status !== 'verified_edu' || originalTotal === null) {
        return { discountedTotal: originalTotal, hasDiscount: false, originalTotal };
    }

    const educationTotal = EDUCATION_PLAN_PRICING[interval] * seats;
    const hasDiscount = educationTotal < originalTotal;

    return {
        discountedTotal: hasDiscount ? educationTotal : originalTotal,
        hasDiscount,
        originalTotal,
    };
};
